import { Injectable, inject } from '@angular/core';
import { SelectOption, ZnSelectQueryParams } from '@zendra/shared';
import { StandService } from 'cbd-proxies/planning/proxy';
import { GetStandsInputDto, StandDto } from 'projects/cbd-proxies/src/lib/planning/dtos/stand';
import { catchError, map, Observable, of } from 'rxjs';

export type StandFilterOptionsResult =
  | SelectOption[]
  | { items: SelectOption[]; totalCount: number };

const STAND_FILTER_PAGE_SIZE = 50;

@Injectable({
  providedIn: 'root',
})
export class StandFilterLoader {
  private readonly standService = inject(StandService);

  load(params?: ZnSelectQueryParams): Observable<StandFilterOptionsResult> {
    const standIds = this.toNumberList(params?.ids);

    if (standIds.length) {
      return this.standService.getListByIds({ ids: standIds }).pipe(
        map(items => this.mapStandFilterOptions(items)),
        catchError(() => of([])),
      );
    }

    const search = params?.search?.trim();
    const request: GetStandsInputDto = {
      name: search || undefined,
      locationIsActive: null,
      skipCount: params?.skipCount ?? 0,
      maxResultCount: params?.maxResultCount ?? STAND_FILTER_PAGE_SIZE,
    };

    return this.standService.getList(request).pipe(
      map(response => ({
        items: this.mapStandFilterOptions(response.items ?? []),
        totalCount: response.totalCount ?? 0,
      })),
      catchError(() => of({
        items: [],
        totalCount: 0,
      })),
    );
  }

  private mapStandFilterOptions(stands: StandDto[]): SelectOption[] {
    const options = new Map<string, string>();

    stands.forEach(stand => {
      if (stand.id == null || !stand.name?.trim()) {
        return;
      }

      options.set(String(stand.id), stand.name.trim());
    });

    return Array.from(options.entries()).map(([key, value]) => ({ key, value }));
  }

  private toNumberList(values?: readonly string[] | null): number[] {
    return (values ?? [])
      .map(value => this.toNumber(value))
      .filter((value): value is number => value !== undefined);
  }

  private toNumber(value: unknown): number | undefined {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value !== 'string') {
      return undefined;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return undefined;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
}
